import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { StreakProvider } from "@/context/StreakContext";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import FAQ from "@/pages/FAQ";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Subjects from "@/pages/Subjects";
import ConceptDetail from "@/pages/ConceptDetail";
import LinkIt from "@/pages/LinkIt";
import QuestionBank from "@/pages/QuestionBank";
import CARS from "@/pages/CarsAcademy";
import MathPractice from "@/pages/MathPractice";
import Equations from "@/pages/Equations";
import PracticeTest from "@/pages/PracticeTest";
import TestSection from "@/pages/TestSection";
import MyPassages from "@/pages/MyPassages";
import SciencePractice from "@/pages/SciencePractice";
import Flashcards from "@/pages/Flashcards";
import Physiology from "@/pages/Physiology";
import Homework from "@/pages/Homework";
import Decks from "@/pages/Decks";
import DeckViewer from "@/pages/DeckViewer";
import Progress from "@/pages/Progress";
import Feedback from "@/pages/Feedback";
import Library from "@/pages/Library";
import ChartsHub from "@/pages/ChartsHub";
import ChartViewer from "@/pages/ChartViewer";
import Plans from "@/pages/Plans";
import StudyPlan from "@/pages/StudyPlan";
import CoachHistory from "@/pages/CoachHistory";
import AdminFeedback from "@/pages/AdminFeedback";
import PlaygroundAnalytics from "@/pages/PlaygroundAnalytics";
import AdminDashboard from "@/pages/AdminDashboard";
import Security from "@/pages/Security";
import CommunityBoard from "@/pages/CommunityBoard";
import "@/App.css";

function Protected({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/auth" replace />;
  return (
    <StreakProvider>
      <Layout>{children}</Layout>
    </StreakProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <Toaster position="top-right" />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/app" element={<Protected><Dashboard /></Protected>} />
              <Route path="/app/subjects" element={<Protected><Subjects /></Protected>} />
              <Route path="/app/subjects/:subjectId" element={<Protected><Subjects /></Protected>} />
              <Route path="/app/topic/:topicId" element={<Protected><ConceptDetail /></Protected>} />
              <Route path="/app/link-it" element={<Protected><LinkIt /></Protected>} />
              <Route path="/app/questions" element={<Protected><QuestionBank /></Protected>} />
              <Route path="/app/cars" element={<Protected><CARS /></Protected>} />
              <Route path="/app/math" element={<Protected><MathPractice /></Protected>} />
              <Route path="/app/equations" element={<Protected><Equations /></Protected>} />
              <Route path="/app/practice-test" element={<Protected><TestSection /></Protected>} />
              <Route path="/app/practice-test/my-passages" element={<Protected><MyPassages /></Protected>} />
              <Route path="/app/practice-test/full" element={<Protected><PracticeTest /></Protected>} />
              <Route path="/app/practice-test/session/:sessionId" element={<Protected><SciencePractice /></Protected>} />
              <Route path="/app/flashcards" element={<Protected><Flashcards /></Protected>} />
              <Route path="/app/physiology" element={<Protected><Physiology /></Protected>} />
              <Route path="/app/homework" element={<Protected><Homework /></Protected>} />
              <Route path="/app/decks" element={<Protected><Decks /></Protected>} />
              <Route path="/app/decks/:deckId" element={<Protected><DeckViewer /></Protected>} />
              <Route path="/app/progress" element={<Protected><Progress /></Protected>} />
              <Route path="/app/library" element={<Protected><Library /></Protected>} />
              <Route path="/app/reference" element={<Protected><ChartsHub /></Protected>} />
              <Route path="/app/reference/:chartId" element={<Protected><ChartViewer /></Protected>} />
              <Route path="/app/plans" element={<Protected><Plans /></Protected>} />
              <Route path="/app/plan" element={<Protected><StudyPlan /></Protected>} />
              <Route path="/app/coach/history" element={<Protected><CoachHistory /></Protected>} />
              <Route path="/app/admin/feedback" element={<Protected><AdminFeedback /></Protected>} />
              <Route path="/app/admin/playground" element={<Protected><PlaygroundAnalytics /></Protected>} />
              <Route path="/app/admin/dashboard" element={<Protected><AdminDashboard /></Protected>} />
              <Route path="/app/feedback" element={<Protected><Feedback /></Protected>} />
              <Route path="/app/community" element={<Protected><CommunityBoard /></Protected>} />
              <Route path="/app/security" element={<Protected><Security /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
